import { AlertCircle, Book, Star, StarOff } from 'lucide-react';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryErrorResetBoundary } from '@tanstack/react-query';

import { useIsAllowed } from '@/app/navigation/access';
import { createLink } from '@/app/navigation/stack-links';
import { useIsPathActive } from '@/app/navigation/useActivePath';
import {
  NavRowContextMenu,
  PinToggleAction,
} from '@/components/Sidebar/nav-pin-controls';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/Sidebar/sidebar';
import {
  useFollowedProjects,
  useUnfollowProject,
} from '@/features/projects/projects.hooks';
import { useCurrentUser } from '@/features/users/users.hooks';

const SidebarMenuLink = createLink(SidebarMenuButton);

/** Wrapper with error boundary - prevents sidebar crashes */
export function NavProjects() {
  // Following is an internal concept; externals see only the projects list.
  const { internal } = useIsAllowed();
  if (!internal) return null;

  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          fallbackRender={({ error }) => <NavProjectsError error={error} />}
          onReset={reset}
          onError={(error) => {
            // Log but don't propagate - this is a non-critical UI component
            console.warn(
              '[NavProjects] Error caught by boundary:',
              error.message,
            );
          }}
        >
          <NavProjectsContent />
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

function NavProjectsError({ error }: { error?: Error }) {
  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel>Followed Projects</SidebarGroupLabel>
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton disabled>
            <AlertCircle className="text-muted-foreground size-4" />
            <span className="text-muted-foreground">
              {error?.message === 'Unauthorized'
                ? 'Session expired'
                : 'Failed to load'}
            </span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
}

function NavProjectsContent() {
  const isActive = useIsPathActive();
  // throw Error('snneses')
  const currentUser = useCurrentUser();
  const { data: followedProjects = [], isLoading } = useFollowedProjects();

  const { mutate: unfollowProject } = useUnfollowProject();

  // Don't render anything if user is not authenticated
  if (!currentUser) {
    return null;
  }

  if (isLoading) {
    return (
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel>Followed Projects</SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton disabled>
              <span className="text-muted-foreground">Loading...</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  if (followedProjects.length === 0) {
    return (
      <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel>Followed Projects</SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton disabled>
              <StarOff className="text-muted-foreground" />
              <span className="text-muted-foreground">
                No followed projects
              </span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    );
  }

  return (
    <SidebarGroup className="group-data-[collapsible=icon]:hidden">
      <SidebarGroupLabel>Followed Projects</SidebarGroupLabel>
      <SidebarMenu>
        {followedProjects.map((project) => {
          const pin = {
            url: `/projects/${project.recnum}`,
            title: project.projectname,
            icon: 'Book',
          };
          return (
            // Touch gets the same long-press sheet as every other menu row —
            // including Pin, which the "More" dropdown never offered.
            <NavRowContextMenu
              key={project.recnum}
              item={pin}
              extraActions={[
                {
                  label: 'Unfollow project',
                  icon: Star,
                  onAction: () =>
                    unfollowProject({
                      projectId: project.recnum,
                      userId: currentUser.recnum,
                    }),
                },
              ]}
            >
              <SidebarMenuItem>
                <SidebarMenuLink
                  to={pin.url}
                  isActive={isActive(pin.url)}
                  className="md:pr-8"
                >
                  <Book className="size-4" />
                  <span className="truncate">{project.projectname}</span>
                </SidebarMenuLink>
                <PinToggleAction item={pin} />
              </SidebarMenuItem>
            </NavRowContextMenu>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
